import React, { useState } from "react";
import { DollarSign, Calendar, TrendingUp, Clock, AlertCircle, CheckCircle, CreditCard, Plus, ChevronDown, ChevronUp } from "lucide-react";
import RecordPaymentModal from "./RecordPaymentModal";
import { fmtUSD } from "../../utils/currency";

const fmt = (v) => {
  if (v == null) return "--";
  if (typeof v === "number") return fmtUSD(v);
  return v;
};

const fmtDollars = (v) => {
  if (v == null) return "--";
  return fmtUSD(v);
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "--";

// ── Payment History Section ──
function PaymentHistory({ payments }) {
  const [expanded, setExpanded] = useState(false);
  if (!payments || !payments.history || payments.history.length === 0) return null;

  const visiblePayments = expanded ? payments.history : payments.history.slice(0, 3);

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="payment-history">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Payment History ({payments.payment_count})</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-emerald-600">{fmtDollars(payments.total_paid)} total</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Metric icon={DollarSign} label="Interest Paid" value={fmtDollars(payments.interest_paid)} />
        <Metric icon={DollarSign} label="Principal Paid" value={fmtDollars(payments.principal_paid)} />
      </div>

      {/* Payment Timeline */}
      <div className="space-y-0 relative">
        <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-emerald-100" />
        {visiblePayments.map((p, i) => {
          const typeColor = p.payment_type === "interest" ? "bg-amber-500" : p.payment_type === "principal" ? "bg-emerald-500" : "bg-blue-500";
          const isLate = p.notes?.toLowerCase().includes("late");
          return (
            <div key={p.payment_id} className="flex items-start gap-3 py-2 relative z-10" data-testid={`payment-${i}`}>
              <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 border-2 ${isLate ? "border-red-300 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
                {isLate ? <AlertCircle className="h-3 w-3 text-red-500" /> : <CheckCircle className="h-3 w-3 text-emerald-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#333]">{fmtDollars(p.amount)}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold capitalize ${p.payment_type === "interest" ? "bg-amber-50 text-amber-600 border border-amber-200" : "bg-emerald-50 text-emerald-600 border border-emerald-200"}`}>{p.payment_type}</span>
                    {isLate && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-red-50 text-red-600 border border-red-200">Late</span>}
                  </div>
                  <span className="text-[10px] text-[#888]">{fmtDate(p.payment_date)}</span>
                </div>
                {p.notes && <p className="text-[10px] text-[#999] mt-0.5 truncate">{p.notes}</p>}
              </div>
            </div>
          );
        })}
      </div>
      {payments.history.length > 3 && (
        <button onClick={() => setExpanded(!expanded)} className="mt-2 flex items-center gap-1 text-xs text-emerald-600 font-medium hover:text-emerald-700" data-testid="toggle-payment-history">
          {expanded ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Show all {payments.payment_count} payments</>}
        </button>
      )}
    </div>
  );
}


// ── Deal Servicing Panel ──
export function DealServicingPanel({ servicing, isAdmin, token, leadId, dealNumber, onPaymentRecorded }) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  if (!servicing) return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center" data-testid="servicing-not-funded">
      <AlertCircle className="h-6 w-6 text-[#ccc] mx-auto mb-2" />
      <p className="text-sm text-[#888]">Servicing data is available after the deal is funded.</p>
    </div>
  );

  const { loan_summary, payment_schedule, balance, payments } = servicing;
  const ls = loan_summary || {};
  const bal = balance || {};
  const nextPmt = bal.next_payment;

  // Delinquency check
  const isOverdue = nextPmt && nextPmt.due_date && new Date(nextPmt.due_date) < new Date();
  const daysPastDue = isOverdue ? Math.floor((new Date() - new Date(nextPmt.due_date)) / 86400000) : 0;

  // Loan progress
  const fundedAmt = ls.funded_amount || payments?.remaining_principal || 0;
  const totalPaid = payments?.total_paid || 0;
  const termMonths = ls.term_months || 12;
  const paymentsMade = payments?.payment_count || 0;
  const termProgress = Math.min(100, (paymentsMade / termMonths) * 100);

  return (
    <div className="space-y-4" data-testid="deal-servicing-panel">
      {/* Delinquency Alert */}
      {isOverdue && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 flex items-center gap-3" data-testid="delinquency-alert">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertCircle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <div className="text-sm font-bold text-red-700">Payment Overdue — {daysPastDue} Day{daysPastDue !== 1 ? "s" : ""} Past Due</div>
            <p className="text-xs text-red-600 mt-0.5">Next payment of {fmt(nextPmt.total_due)} was due {fmtDate(nextPmt.due_date)}</p>
          </div>
        </div>
      )}

      {/* Admin: Record Payment Button */}
      {isAdmin && (
        <button
          onClick={() => setShowPaymentModal(true)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors shadow-sm"
          data-testid="mark-payment-received-btn"
        >
          <Plus className="h-4 w-4" /> Mark Payment Received
        </button>
      )}

      {/* Loan Progress Bar */}
      {fundedAmt > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="loan-progress">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Loan Progress</h3>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-[#555]">Term Progress</span>
                <span className="font-bold text-[#222]">{paymentsMade} / {termMonths} months</span>
              </div>
              <div className="h-2.5 bg-[#f0ece4] rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${termProgress}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-[#555]">Principal Repayment</span>
                <span className="font-bold text-[#222]">{fmtDollars(payments?.principal_paid || 0)} / {fmtDollars(fundedAmt)}</span>
              </div>
              <div className="h-2.5 bg-[#f0ece4] rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${fundedAmt > 0 ? Math.min(100, ((payments?.principal_paid || 0) / fundedAmt) * 100) : 0}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History */}
      {payments && payments.payment_count > 0 && <PaymentHistory payments={payments} />}

      {/* Real Balance (if payments exist) */}
      {payments && payments.payment_count > 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="real-balance-card">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-3">Current Balance (Real)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="text-center p-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-emerald-700">{fmtDollars(payments.remaining_principal)}</div>
              <div className="text-[9px] text-emerald-600">Remaining Principal</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-emerald-700">{fmtDollars(payments.total_paid)}</div>
              <div className="text-[9px] text-emerald-600">Total Paid</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-emerald-700">{payments.payment_count}</div>
              <div className="text-[9px] text-emerald-600">Payments Made</div>
            </div>
          </div>
        </div>
      )}

      {/* Loan Summary */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Loan Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric icon={DollarSign} label="Funded Amount" value={fmt(ls.funded_amount)} accent />
          <Metric icon={TrendingUp} label="Rate" value={ls.annual_rate ? `${ls.annual_rate}%` : "--"} />
          <Metric icon={Calendar} label="Term" value={ls.term_months ? `${ls.term_months} mo` : "--"} />
          <Metric icon={Calendar} label="Maturity" value={fmtDate(ls.maturity_date)} />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Metric icon={CreditCard} label="Monthly Payment" value={fmt(ls.monthly_payment)} />
          <Metric icon={DollarSign} label="Total Interest" value={fmt(ls.total_interest)} />
        </div>
      </div>

      {/* Next Payment Due */}
      {nextPmt && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="next-payment-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-bold text-amber-800">Next Payment Due</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="text-sm font-bold text-amber-900">{fmtDate(nextPmt.due_date)}</div>
              <div className="text-[9px] text-amber-600">Due Date</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-bold text-amber-900">{fmt(nextPmt.interest_due)}</div>
              <div className="text-[9px] text-amber-600">Interest</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-bold text-amber-900">{fmt(nextPmt.total_due)}</div>
              <div className="text-[9px] text-amber-600">Total Due</div>
            </div>
          </div>
        </div>
      )}

      {/* Outstanding Balance (projected) */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="balance-card">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Projected Balance</h3>
        <div className="grid grid-cols-2 gap-3">
          <Metric icon={DollarSign} label="Original Principal" value={fmt(ls.funded_amount)} />
          <Metric icon={TrendingUp} label="Interest Accrued" value={fmt(bal.total_interest_accrued)} />
        </div>
      </div>

      {/* Payment Schedule */}
      {payment_schedule && payment_schedule.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="payment-schedule">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Amortization Schedule</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#e8e0d0]">
                  <th className="text-left py-2 text-[10px] text-[#999] font-medium">#</th>
                  <th className="text-left py-2 text-[10px] text-[#999] font-medium">Due Date</th>
                  <th className="text-right py-2 text-[10px] text-[#999] font-medium">Interest</th>
                  <th className="text-right py-2 text-[10px] text-[#999] font-medium">Principal</th>
                  <th className="text-right py-2 text-[10px] text-[#999] font-medium">Total</th>
                  <th className="text-center py-2 text-[10px] text-[#999] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {payment_schedule.map(pmt => {
                  const isPaid = pmt.payment_number <= paymentsMade;
                  const isCurrent = pmt.payment_number === paymentsMade + 1;
                  const isPastDue = isCurrent && isOverdue;
                  return (
                    <tr key={pmt.payment_number} className={`border-b border-[#f5f0e8] ${pmt.is_maturity ? "bg-emerald-50 font-semibold" : isPastDue ? "bg-red-50" : isCurrent ? "bg-amber-50" : ""}`}>
                      <td className="py-1.5 text-[#666]">{pmt.payment_number}</td>
                      <td className="py-1.5 text-[#333]">{fmtDate(pmt.due_date)}</td>
                      <td className="py-1.5 text-right text-[#333]">{fmt(pmt.interest_due)}</td>
                      <td className="py-1.5 text-right text-[#333]">{pmt.principal_due > 0 ? fmt(pmt.principal_due) : "--"}</td>
                      <td className="py-1.5 text-right font-medium text-[#222]">{fmt(pmt.total_due)}</td>
                      <td className="py-1.5 text-center">
                        {isPaid ? (
                          <span className="inline-flex items-center gap-0.5 text-[9px] px-2 py-0.5 rounded-full font-bold bg-emerald-50 text-emerald-600 border border-emerald-200"><CheckCircle className="h-2.5 w-2.5" /> Paid</span>
                        ) : isPastDue ? (
                          <span className="inline-flex items-center gap-0.5 text-[9px] px-2 py-0.5 rounded-full font-bold bg-red-50 text-red-600 border border-red-200"><AlertCircle className="h-2.5 w-2.5" /> Overdue</span>
                        ) : isCurrent ? (
                          <span className="inline-flex items-center gap-0.5 text-[9px] px-2 py-0.5 rounded-full font-bold bg-amber-50 text-amber-600 border border-amber-200"><Clock className="h-2.5 w-2.5" /> Due</span>
                        ) : (
                          <span className="text-[9px] text-[#ccc]">Upcoming</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <RecordPaymentModal
          leadId={leadId}
          dealNumber={dealNumber}
          token={token}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={(data) => {
            setShowPaymentModal(false);
            onPaymentRecorded && onPaymentRecorded(data);
          }}
        />
      )}
    </div>
  );
}

// ── Lender Servicing Panel ──
export function LenderServicingPanel({ servicing }) {
  if (!servicing) return null;

  const { total_funded, total_expected_interest, payments_received, remaining_principal, active_loans } = servicing;

  return (
    <div className="space-y-4" data-testid="lender-servicing-panel">
      {total_funded > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Servicing Summary</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric icon={DollarSign} label="Total Funded" value={fmt(total_funded)} accent />
            <Metric icon={TrendingUp} label="Expected Interest" value={fmt(total_expected_interest)} />
            <Metric icon={CheckCircle} label="Payments Received" value={fmtDollars(payments_received)} />
            <Metric icon={DollarSign} label="Remaining Principal" value={fmt(remaining_principal)} />
          </div>
        </div>
      )}

      {active_loans && active_loans.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="lender-active-loans">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Active Loans ({active_loans.length})</h3>
          <div className="space-y-2">
            {active_loans.map((loan, i) => (
              <div key={loan.lead_id || i} className="rounded-lg border border-[#f0ebe0] p-3 bg-[#faf7f2]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#222]">{loan.deal_number || loan.lead_id?.slice(0, 8)}</span>
                  <span className="text-[10px] text-[#888]">{loan.rate}% / {loan.term_months}mo</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div><div className="text-xs font-semibold text-[#333]">{fmt(loan.funded_amount)}</div><div className="text-[9px] text-[#999]">Funded</div></div>
                  <div><div className="text-xs font-semibold text-[#333]">{fmt(loan.monthly_payment)}</div><div className="text-[9px] text-[#999]">Monthly</div></div>
                  <div><div className="text-xs font-semibold text-emerald-600">{fmtDollars(loan.payments_received || 0)}</div><div className="text-[9px] text-[#999]">Received</div></div>
                  <div><div className="text-xs font-semibold text-[#333]">{fmtDate(loan.maturity_date)}</div><div className="text-[9px] text-[#999]">Maturity</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!active_loans || active_loans.length === 0) && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center">
          <p className="text-sm text-[#888]">No active servicing positions. Funded deals will appear here.</p>
        </div>
      )}
    </div>
  );
}

// ── Investor Servicing Panel ──
export function InvestorServicingPanel({ servicing }) {
  if (!servicing) return null;

  const { committed_capital, deployed_capital, distributions_received, principal_returned, interest_earned, active_investments, completed_investments, positions } = servicing;

  return (
    <div className="space-y-4" data-testid="investor-servicing-panel">
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Servicing Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric icon={DollarSign} label="Committed Capital" value={fmt(committed_capital)} accent />
          <Metric icon={TrendingUp} label="Deployed Capital" value={fmt(deployed_capital)} />
          <Metric icon={CheckCircle} label="Distributions" value={fmt(distributions_received)} />
          <Metric icon={TrendingUp} label="Interest Earned" value={fmt(interest_earned)} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <Metric icon={DollarSign} label="Principal Returned" value={fmt(principal_returned)} />
          <div className="text-center p-2 rounded-lg bg-[#faf7f2]"><div className="text-sm font-bold text-emerald-600">{active_investments}</div><div className="text-[9px] text-[#999]">Active</div></div>
          <div className="text-center p-2 rounded-lg bg-[#faf7f2]"><div className="text-sm font-bold text-[#333]">{completed_investments}</div><div className="text-[9px] text-[#999]">Completed</div></div>
        </div>
      </div>

      {positions && positions.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="investor-positions">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Investment Positions ({positions.length})</h3>
          <div className="space-y-2">
            {positions.map((pos, i) => (
              <div key={pos.commitment_id || i} className="rounded-lg border border-[#f0ebe0] p-3 bg-[#faf7f2]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#222]">{pos.loan_type || "Investment"}</span>
                    {pos.state && <span className="text-[10px] text-[#888]">{pos.state}</span>}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${pos.status === "funded" ? "bg-emerald-100 text-emerald-600" : pos.status === "pending" ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-600"}`}>{pos.status}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-xs font-semibold text-[#333]">{fmt(pos.amount)}</div><div className="text-[9px] text-[#999]">Committed</div></div>
                  <div><div className="text-xs font-semibold text-emerald-600">{pos.projected_yield ? `${pos.projected_yield}%` : "--"}</div><div className="text-[9px] text-[#999]">Yield</div></div>
                  <div><div className="text-xs font-semibold text-[#333]">{fmt(pos.distributions_received)}</div><div className="text-[9px] text-[#999]">Distributed</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!positions || positions.length === 0) && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center">
          <p className="text-sm text-[#888]">No active investment positions yet.</p>
        </div>
      )}
    </div>
  );
}

// ── Shared Metric Component ──
function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="text-center p-2 rounded-lg bg-[#faf7f2]">
      {Icon && <Icon className={`h-3.5 w-3.5 mx-auto mb-1 ${accent ? "text-emerald-600" : "text-[#999]"}`} />}
      <div className={`text-sm font-bold ${accent ? "text-emerald-700" : "text-[#222]"}`}>{value}</div>
      <div className="text-[9px] text-[#999]">{label}</div>
    </div>
  );
}
